import { useEffect, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import type { PaddleProductPayload } from '@vy/api-definition';
import { fromNullToUndefined } from '@vy/utils';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../../../../../../components/new-design/feedback/VyDialog';
import { useModifyExternalProduct } from '../../../../../../../../../../../hooks/requests/integrations';
import { runMutation } from '../../../../../../../../../../../utils/vy-mutation';

type EditPaddleProductDialogProps = {
  open: boolean;
  onClose: () => void;
  productId: string;
  externalProductId: string | undefined;
  paddleProducts: PaddleProductPayload[];
};

export const EditPaddleProductDialog = ({
  open,
  onClose,
  productId,
  externalProductId,
  paddleProducts,
}: EditPaddleProductDialogProps) => {
  const modifyExternalProductMutation = useModifyExternalProduct(productId);
  const queryClient = useQueryClient();

  const [selectedPaddleProductId, setSelectedPaddleProductId] = useState<string | null>(
    externalProductId ?? null,
  );

  useEffect(() => {
    if (open) {
      setSelectedPaddleProductId(externalProductId ?? null);
    }
  }, [open, externalProductId]);

  const selectedPaddleProduct = paddleProducts.find(
    (product) => product.id === selectedPaddleProductId,
  );

  const handleSubmit = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedPaddleProductId),
        integration: INTEGRATION_PROVIDERS.paddle,
        productId,
      },
      {
        pending: 'Updating product...',
        success: 'Product updated successfully',
        error: 'Failed to update product',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) && query.queryKey[0] === 'invoice-products-integrations',
          });
          onClose();
        },
      },
    );
  };

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Edit Paddle Product"
      cancelButtonText="Cancel"
      ctaText="Save"
      onSubmit={handleSubmit}
    >
      <Stack>
        <Autocomplete
          options={paddleProducts ?? []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPaddleProduct}
          onChange={(event, value) => setSelectedPaddleProductId(value?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Paddle Product" />}
        />
      </Stack>
    </VyDialog>
  );
};
